import { useParams, Link, useNavigate } from "@/lib/router-compat";
import FallbackTitle from "@/components/FallbackTitle";
import SsrLinkGrid from "@/components/SsrLinkGrid";
import type { ListingLink } from "@/lib/listingLinks";
import { jsonLd } from "@/lib/seoHelpers";
import { getCategoryPath } from "@/lib/categoryRedirects";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import parse, { DOMNode, Element, domToReact } from "html-react-parser";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RelatedArticles, { type RelatedArticle as RelatedArticleItem } from "@/components/RelatedArticles";
import ArticleComments from "@/components/ArticleComments";
import ShareButtons from "@/components/ShareButtons";
import PreferredSourceButton from "@/components/PreferredSourceButton";
import { InArticleAd, SidebarAd } from "@/components/AdSense";
import SidebarRelatedArticles from "@/components/SidebarRelatedArticles";
import SidebarCustomAds from "@/components/SidebarCustomAds";
import { PriceDropsWidget } from "@/components/PriceDropsWidget";
import AmazonProductCard from "@/components/AmazonProductCard";
import InstagramEmbed from "@/components/InstagramEmbed";
import InternalProductCard, { type InternalProductType } from "@/components/InternalProductCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, User, ArrowLeft, Calendar, ChevronRight, ChevronDown, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useAutoLinker } from "@/hooks/useAutoLinker";
import { useAmazonPriceAugment } from "@/hooks/useAmazonPriceAugment";
import { useAffiliatePriceAugment } from "@/hooks/useAffiliatePriceAugment";
import { useRef } from "react";
// BreadcrumbSchema not imported — Article.tsx emits its own inline BreadcrumbList JSON-LD below.
import ArticleListenButton from "@/components/ArticleListenButton";
import MatchingProductBox from "@/components/MatchingProductBox";
import KeyTakeaways from "@/components/KeyTakeaways";
import { logNotFound } from "@/lib/logNotFound";
import type { Tables } from "@/integrations/supabase/types";
import {
  articleQueryOptions,
  getArticlePath,
  isPlantGuide,
  getArticleRedirectTarget,
  type ArticleWithAuthor as LibArticleWithAuthor,
} from "@/lib/articleQuery";

// Detect if content is HTML or Markdown
const isHtmlContent = (content: string | null): boolean => {
  if (!content) return false;
  // Check for common HTML tags
  return /<(p|div|figure|img|a|ul|ol|h[1-6]|table|blockquote|span)\b[^>]*>/i.test(content);
};

interface FAQItem {
  question: string;
  answer: string;
}

type ArticleWithAuthor = LibArticleWithAuthor;


// Get FAQs from structured data or fallback to content extraction
const getFAQs = (faqs: unknown, content: string | null): FAQItem[] => {
  // First, try to use structured FAQs from database
  if (faqs && Array.isArray(faqs) && faqs.length > 0) {
    return faqs.filter((faq): faq is FAQItem => 
      typeof faq === 'object' && 
      faq !== null && 
      'question' in faq && 
      'answer' in faq &&
      typeof faq.question === 'string' &&
      typeof faq.answer === 'string' &&
      faq.question.trim() !== '' &&
      faq.answer.trim() !== ''
    ).slice(0, 10);
  }
  
  // Fallback: Extract FAQs from content
  if (!content) return [];
  
  const extractedFaqs: FAQItem[] = [];
  
  // Look for FAQ section and extract Q&A pairs
  const faqSectionMatch = content.match(/##\s*(?:Frequently Asked Questions|FAQ|FAQs)[\s\S]*$/i);
  if (!faqSectionMatch) return [];
  
  const faqSection = faqSectionMatch[0];
  
  // Match ### Question? followed by answer paragraph
  const questionPattern = /###\s*([^\n]+\?)\s*\n+([^#]+?)(?=###|$)/gi;
  let match;
  
  while ((match = questionPattern.exec(faqSection)) !== null) {
    const question = match[1].trim();
    const answer = match[2].trim().replace(/\n+/g, ' ').substring(0, 500);
    if (question && answer) {
      extractedFaqs.push({ question, answer });
    }
  }
  
  return extractedFaqs.slice(0, 10);
};

// Extract HowTo steps from markdown content
const extractHowToSteps = (content: string | null): { name: string; text: string }[] => {
  if (!content) return [];
  
  const steps: { name: string; text: string }[] = [];
  
  // Look for numbered step patterns like "Step 1:" or "### Step 1:"
  const stepPattern = /(?:###?\s*)?(?:Step\s*)?(\d+)[\.:]\s*([^\n]+)\n+([^#\n]+(?:\n(?!###?\s*(?:Step\s*)?\d)[^#\n]+)*)/gi;
  let match;
  
  while ((match = stepPattern.exec(content)) !== null) {
    const stepName = match[2].trim();
    const stepText = match[3].trim().replace(/\n+/g, ' ').substring(0, 500);
    if (stepName && stepText) {
      steps.push({ name: stepName, text: stepText });
    }
  }
  
  return steps.slice(0, 20); // Limit to 20 steps
};

const Article = ({
  initialArticle,
  ssrLinks = [],
  relatedArticles,
}: {
  initialArticle?: ArticleWithAuthor | null;
  ssrLinks?: ListingLink[];
  relatedArticles?: RelatedArticleItem[];
}) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Normalize/redirect legacy article slugs before querying
  useEffect(() => {
    if (!slug) return;

    const redirectTarget = getArticleRedirectTarget(slug);
    if (redirectTarget) {
      navigate(`/articles/${redirectTarget}`, { replace: true });
    }
  }, [slug, navigate]);

  // initialArticle comes from the route loader so the article is rendered
  // server-side (real <h1> and body copy in the HTML crawlers receive).
  const { data: article, isLoading, error } = useQuery({
    ...articleQueryOptions(slug!),
    enabled: !!slug,
    ...(initialArticle !== undefined ? { initialData: initialArticle } : {}),
  });



  useEffect(() => {
    if (!article?.slug) return;
    const target = getArticlePath(article);
    if (article.slug !== slug || (typeof window !== "undefined" && window.location.pathname !== target)) {
      navigate(target, { replace: true });
    }
  }, [article, slug, navigate]);

  // Auto-link keywords in content
  const { linkedContent } = useAutoLinker(article?.content ?? null, slug);
  const articleBodyRef = useRef<HTMLDivElement>(null);
  useAmazonPriceAugment(articleBodyRef, linkedContent);
  useAffiliatePriceAugment(articleBodyRef, linkedContent);

  const estimateReadTime = (content: string | null) => {
    if (!content) return "5 min read";
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  // Generate structured data with enhanced properties for AI/voice search
  const generateStructuredData = () => {
    if (!article) return null;

    const baseUrl = "https://www.garden-review.co.uk";
    const articleUrl = `${baseUrl}${getArticlePath(article)}`;
    const authorProfile = article.authors;
    
    const schemas: object[] = [];

    // Calculate word count
    const wordCount = article.content 
      ? article.content.replace(/<[^>]*>/g, '').split(/\s+/).length 
      : undefined;

    // Article Schema with enhanced properties for AI Search
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${articleUrl}#article`,
      "headline": article.title,
      "description": article.excerpt || article.title,
      "image": article.featured_image ? {
        "@type": "ImageObject",
        "url": article.featured_image,
        "caption": article.featured_image_alt || article.title
      } : undefined,
      "author": {
        "@type": "Person",
        "name": authorProfile?.name || article.author || "Garden-Review",
        "url": authorProfile?.slug ? `${baseUrl}/authors/${authorProfile.slug}` : baseUrl
      },
      "publisher": {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        "name": "Garden-Review",
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/favicon.png`
        }
      },
      "datePublished": article.published_at || article.created_at,
      "dateModified": article.updated_at,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl
      },
      "inLanguage": "en-GB",
      "isAccessibleForFree": true,
      "keywords": article.tags?.join(", ") || undefined,
      "articleSection": article.category,
      "wordCount": wordCount,
      // Speakable specification for voice search and AI assistants
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [
          "article h1",
          "[data-speakable='true']",
          ".article-excerpt"
        ]
      }
    };
    schemas.push(articleSchema);

    // BreadcrumbList for navigation context
    const hub = isPlantGuide(article)
      ? { name: "Plant Guides", path: "/plants" }
      : { name: "Articles", path: "/articles" };
    const breadcrumbItems = [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": hub.name, "item": `${baseUrl}${hub.path}` }
    ];
    
    if (article.category) {
      const categoryPath = getCategoryPath(article.category) || "/articles";
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 3,
        "name": article.category,
        "item": `${baseUrl}${categoryPath}`
      });
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 4,
        "name": article.title
      } as any);
    } else {
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 3,
        "name": article.title
      } as any);
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems
    };
    schemas.push(breadcrumbSchema);

    // FAQPage Schema (if FAQs exist - prioritize structured data from database)
    const faqs = getFAQs((article as any).faqs, article.content);
    if (faqs.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
      schemas.push(faqSchema);
    }

    // HowTo Schema (if step-by-step instructions exist)
    const steps = extractHowToSteps(article.content);
    if (steps.length >= 3) {
      const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": article.title,
        "description": article.excerpt || article.title,
        "image": article.featured_image || undefined,
        "step": steps.map((step, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": step.name,
          "text": step.text
        }))
      };
      schemas.push(howToSchema);
    }

    // Product + Review Schema (if product_rating is set)
    const productRating = (article as any).product_rating;
    const productName = (article as any).product_name;
    if (productRating && productName) {
      const productReviewSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": productName,
        "image": article.featured_image || undefined,
        "description": article.excerpt || article.title,
        "review": {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": productRating,
            "bestRating": "5",
            "worstRating": "1"
          },
          "author": {
            "@type": "Organization",
            "name": "Garden-Review"
          },
          "datePublished": article.published_at || article.created_at,
          "reviewBody": article.excerpt || article.title
        }
      };
      schemas.push(productReviewSchema);
    }

    return schemas;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <FallbackTitle />
        <Header />
        <main className="container mx-auto px-4 lg:px-8 py-12">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="h-80 w-full mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    // Log only once the query has resolved (isLoading is false here) — avoids
    // logging transient loading states. Silent diagnostic insert.
    if (typeof window !== "undefined") {
      void logNotFound("article");
    }
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="prerender-status-code" content="404" />
          <title>Article Not Found | Garden-Review</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="container mx-auto px-4 lg:px-8 py-12">
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold text-foreground mb-4">Article Not Found</h1>
              <p className="text-muted-foreground mb-8">
                The article you're looking for doesn't exist or has been removed.
              </p>
              <Link to="/articles">
                <Button>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Articles
                </Button>
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const imageAlt = article.featured_image_alt || article.title;
  const structuredData = generateStructuredData();
  const authorProfile = article.authors;

  return (
    <div className="min-h-screen bg-background">
      {/* Title/description/canonical/OG tags are emitted server-side by the
          route's head() in src/routes/articles/$slug.tsx — only JSON-LD here. */}
      <Helmet>

        {structuredData && structuredData.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {jsonLd(schema)}
          </script>
        ))}
      </Helmet>
      
      <Header />
      
      <main className="container mx-auto px-4 lg:px-8 py-12">
        {/* Breadcrumb Navigation - semantic markup for SEO */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            </li>
            <li aria-hidden="true"><ChevronRight className="h-4 w-4" /></li>
            <li>
              <Link
                to={isPlantGuide(article) ? "/plants" : "/articles"}
                className="hover:text-primary transition-colors"
              >
                {isPlantGuide(article) ? "Plant Guides" : "Articles"}
              </Link>
            </li>
            {article.category && (
              <>
                <li aria-hidden="true"><ChevronRight className="h-4 w-4" /></li>
                <li>
                  <Link 
                    to={getCategoryPath(article.category) || "/articles"}
                    className="hover:text-primary transition-colors"
                  >
                    {article.category}
                  </Link>
                </li>
              </>
            )}
            <li aria-hidden="true"><ChevronRight className="h-4 w-4" /></li>
            <li className="text-foreground truncate max-w-[200px] md:max-w-none" title={article.title}>
              {article.title}
            </li>
          </ol>
        </nav>

        <div className="flex gap-8">
          <article className="flex-1 min-w-0" itemScope itemType="https://schema.org/Article">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {article.category && (
                  <Badge variant="secondary" className="text-sm" itemProp="articleSection">
                    {article.category}
                  </Badge>
                )}
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{estimateReadTime(article.content)}</span>
                </div>
              </div>

              {/* Title with speakable attribute for AI/voice search */}
              <h1 
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight"
                data-speakable="true"
                itemProp="headline"
              >
                {article.title}
              </h1>

              {/* Excerpt with speakable attribute for AI assistants */}
              {article.excerpt && (
                <div 
                  className="article-excerpt bg-card rounded-xl p-5 md:p-6 border border-border/30 shadow-soft"
                  data-speakable="true"
                  itemProp="description"
                >
                  <p className="text-xl text-foreground leading-relaxed">
                    {article.excerpt}
                  </p>
                </div>
              )}

              {/* Quotable answer block for AI answer engines */}
              <KeyTakeaways content={article.content} title={article.title} />

              {/* Product Rating Display */}
              {(article as any).product_rating && (article as any).product_name && (
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 md:p-6 mt-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Our Rating</p>
                      <p className="text-lg font-bold text-foreground">{(article as any).product_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-6 w-6 ${
                              star <= Math.floor((article as any).product_rating)
                                ? "text-yellow-500 fill-yellow-500"
                                : star - 0.5 <= (article as any).product_rating
                                ? "text-yellow-500 fill-yellow-500/50"
                                : "text-muted-foreground/30"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-2xl font-bold text-foreground ml-2">
                        {(article as any).product_rating}/5
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {!(article as any).hide_product_box &&
                ((article as any).product_name || (article as any).matched_product_slug) && (
                <MatchingProductBox
                  productName={(article as any).product_name}
                  manualHub={(article as any).matched_product_hub}
                  manualSlug={(article as any).matched_product_slug}
                />
              )}

              <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground border-y border-border py-4 mt-4">
                <div className="flex flex-wrap items-center gap-4">
                  {article.author && (
                    <div className="flex items-center gap-2" itemProp="author" itemScope itemType="https://schema.org/Person">
                      <User className="h-4 w-4" />
                      {authorProfile?.slug ? (
                        <Link to={`/authors/${authorProfile.slug}`} className="hover:text-primary transition-colors" itemProp="url">
                          <span itemProp="name">{authorProfile.name || article.author}</span>
                        </Link>
                      ) : (
                        <span itemProp="name">{article.author}</span>
                      )}
                    </div>
                  )}
                  {article.published_at && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <time itemProp="datePublished" dateTime={article.published_at}>
                        {format(new Date(article.published_at), 'MMMM d, yyyy')}
                      </time>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <ArticleListenButton content={article.content} title={article.title} />
                  <ShareButtons 
                    url={`https://www.garden-review.co.uk${getArticlePath(article)}`} 
                    title={article.title}
                    description={article.excerpt || undefined}
                  />
                </div>
              </div>
            </header>

            {article.featured_image && (
              <figure className="mb-10 rounded-lg overflow-hidden">
                {(article as any).featured_image_link ? (
                  <a
                    href={(article as any).featured_image_link}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    aria-label={`Visit link for ${article.title}`}
                  >
                    <img
                      src={article.featured_image}
                      alt={imageAlt}
                      title={article.title}
                      loading="eager"
                      className="w-full max-h-[400px] object-cover object-top hover:opacity-90 transition-opacity cursor-pointer"
                    />
                  </a>
                ) : (
                  <img
                    src={article.featured_image}
                    alt={imageAlt}
                    title={article.title}
                    loading="eager"
                    className="w-full max-h-[400px] object-cover object-top"
                  />
                )}
              </figure>
            )}

            <div className="bg-card rounded-xl p-4 md:p-8 lg:p-10 border border-border/30 shadow-soft overflow-hidden">
              <div ref={articleBodyRef} className="
                prose prose-lg max-w-none overflow-x-auto break-words
                prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-tight
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:border-b prose-h2:border-border prose-h2:pb-3
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
                prose-h4:text-base prose-h4:mt-6 prose-h4:mb-2
                prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                prose-a:text-primary prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-primary/80
                prose-strong:text-foreground prose-strong:font-semibold
                prose-li:text-foreground/80 prose-li:leading-relaxed prose-li:my-1
                prose-ul:my-6 prose-ul:pl-6 prose-ol:my-6 prose-ol:pl-6
                prose-blockquote:text-foreground/70 prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:my-8
                prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono
                prose-pre:bg-muted prose-pre:rounded-lg prose-pre:p-4
                prose-hr:my-10 prose-hr:border-border
                prose-table:border-collapse prose-table:w-full prose-table:my-6
                [&_table]:border [&_table]:border-border [&_table]:rounded-lg [&_table]:overflow-hidden [&_table]:w-full
                [&_th]:border [&_th]:border-border [&_th]:p-3 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground [&_th]:bg-[#f3f4f6]
                [&_td]:border [&_td]:border-border [&_td]:p-3
                [&_tbody_tr:nth-child(even)]:bg-muted/30 [&_tbody_tr:hover]:bg-muted/50 [&_tbody_tr]:transition-colors
                [&_img]:max-w-full [&_img]:w-auto [&_img]:h-auto [&_img]:max-h-[500px] [&_img]:object-cover [&_img]:rounded-lg [&_img]:my-6 [&_img]:mx-auto [&_img]:block
              ">
                {isHtmlContent(linkedContent) ? (
                  // Parse sanitized HTML and inject ad after 2nd paragraph
                  (() => {
                    let paragraphCount = 0;
                    let adInserted = false;
                    let adCount = 0;
                    // Sanitize HTML content to prevent XSS
                    const sanitizedContent = sanitizeHtml(linkedContent);
                    
                    return parse(sanitizedContent, {
                      replace: (domNode) => {
                        if (domNode instanceof Element) {
                          // Instagram embed: pasted blockquote.instagram-media or <div data-instagram-url="...">
                          const igShortcode = domNode.attribs?.['data-instagram-url'];
                          const isIgBlockquote =
                            domNode.name === 'blockquote' &&
                            (domNode.attribs?.class || '').includes('instagram-media');
                          if (igShortcode || isIgBlockquote) {
                            const findAnchor = (nodes: any[]): string | undefined => {
                              for (const child of nodes || []) {
                                if (child instanceof Element) {
                                  if (child.name === 'a' && child.attribs?.href) return child.attribs.href;
                                  const nested = findAnchor(child.children as any[]);
                                  if (nested) return nested;
                                }
                              }
                              return undefined;
                            };
                            const igUrl =
                              igShortcode ||
                              domNode.attribs?.['data-instgrm-permalink'] ||
                              findAnchor(domNode.children as any[]);
                            if (igUrl) return <InstagramEmbed url={igUrl} />;
                          }
                          // Amazon product card shortcode: <div data-amazon-asin="ASIN"></div>
                          const asin = domNode.attribs?.['data-amazon-asin'];
                          if (asin) {
                            return <AmazonProductCard asin={asin} />;
                          }
                          // Internal product card shortcode
                          const productId = domNode.attribs?.['data-product-id'];
                          const productType = domNode.attribs?.['data-product-type'] as InternalProductType | undefined;
                          if (productId && productType) {
                            return <InternalProductCard productId={productId} productType={productType} />;
                          }
                          if (domNode.name === 'p') {
                            paragraphCount++;
                            // Insert an ad after the 2nd paragraph, then every 8
                            // paragraphs (max 3 in-article units per page).
                            const shouldInsert =
                              (paragraphCount === 2 || (paragraphCount > 2 && (paragraphCount - 2) % 8 === 0)) &&
                              adCount < 3;
                            if (shouldInsert) {
                              adCount++;
                              adInserted = true;
                              return (
                                <>
                                  <p>{domToReact(domNode.children as DOMNode[])}</p>
                                  <InArticleAd className="my-8" />
                                </>
                              );
                            }
                          }
                        }
                        return undefined;
                      }
                    });
                  })()
                ) : (
                  // For markdown content, split and inject ad
                  (() => {
                    const paragraphs = (linkedContent || '').split(/\n\n+/);
                    if (paragraphs.length <= 2) {
                      return (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {linkedContent || ''}
                        </ReactMarkdown>
                      );
                    }
                    const firstPart = paragraphs.slice(0, 2).join('\n\n');
                    const secondPart = paragraphs.slice(2).join('\n\n');
                    return (
                      <>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {firstPart}
                        </ReactMarkdown>
                        <InArticleAd className="my-8" />
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {secondPart}
                        </ReactMarkdown>
                      </>
                    );
                  })()
                )}
              </div>
            </div>

            {/* End-of-article ad */}
            <InArticleAd className="my-10" />



          {/* FAQ Section with Accordion */}
          {(() => {
            const articleFaqs = getFAQs((article as any).faqs, null);
            const faqTitle = (article as any).faq_title;
            if (articleFaqs.length === 0) return null;
            return (
              <section className="mt-12 bg-muted/30 rounded-xl p-6 md:p-8 border border-border/30">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                  {faqTitle || "Frequently Asked Questions"}
                </h2>
                {/* Native <details> keeps every answer in the server HTML (Radix accordions drop closed content). */}
                <div className="w-full space-y-2">
                  {articleFaqs.map((faq, index) => (
                    <details
                      key={index}
                      className="group bg-card/50 rounded-lg px-6 border-none"
                    >
                      <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-left text-lg font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                        {faq.question}
                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-open:rotate-180" />
                      </summary>
                      <div className="text-foreground/80 leading-relaxed pb-4">
                        {faq.answer}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            );
          })()}

          {article.tags && article.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag, index) => (
                  <Badge key={index} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Share buttons at end of article */}
          <div className="mt-8 pt-6 border-t border-border">
            <div className="flex flex-col items-center gap-4">
              <ShareButtons 
                url={`https://www.garden-review.co.uk${getArticlePath(article)}`} 
                title={article.title}
                description={article.excerpt || undefined}
              />
              <PreferredSourceButton />
            </div>
          </div>

            <ArticleComments articleId={article.id} />

            <RelatedArticles 
              currentArticleId={article.id}
              category={article.category}
              tags={article.tags}
              initialArticles={relatedArticles}
            />
          </article>
          
          {/* Sidebar */}
          <aside className="hidden lg:block w-72 xl:w-80 shrink-0">
            <div className="sticky top-24 space-y-6">
              <SidebarAd />
              <SidebarCustomAds articleAdIds={(article as any).sidebar_ad_ids || []} />
              <PriceDropsWidget variant="sidebar" />
              {(article as any).sidebar_show_related !== false && (
                <SidebarRelatedArticles
                  currentArticleId={article.id}
                  category={article.category}
                  tags={article.tags}
                  pinnedIds={(article as any).sidebar_related_ids || []}
                />
              )}
              <SidebarAd />
            </div>
          </aside>
        </div>
      </main>

      <SsrLinkGrid
        title="More from this category"
        description="Further reviews and guides you can read next."
        links={ssrLinks}
      />

      <Footer />

    </div>
  );
};

export default Article;
